import {AutocompleteInput, ReferenceInput, required} from 'react-admin';
import {
    CallbackProps,
    DisplayProps,
    IReferenceInputProps,
    ReferenceProps,
    ValidationProps,
} from '../../models/input-field.models';
import {EMPTY_FILTER_ID} from '../../config/statuses';
import React, {useEffect, useState} from 'react';
import {useWatch} from 'react-hook-form';
import {ReferenceInputWithLink} from './referenceInputWithLink';
import CreateWebsiteFromDropdown from './createWebsiteFromDropdown';

export const UserInput = ({
    source,
    label,
    isRequired,
    disabled,
    placeholder,
    defaultValue,
    variant,
    helperText,
}: ReferenceProps & ValidationProps & DisplayProps) => {
    return (
        <ReferenceInput
            source={source}
            reference="v1_SystemUserBase"
            filter={{'Status#name': 'Active'}}
            sort={{field: 'name', order: 'ASC'}}
        >
            <AutocompleteInput
                label={label}
                disabled={disabled}
                optionText="name"
                emptyText={placeholder}
                defaultValue={defaultValue}
                filterToQuery={(searchText) => ({name: searchText})}
                parse={(value) => (value === '' ? null : value)}
                validate={isRequired ? required() : undefined}
                variant={variant}
                helperText={helperText}
            />
        </ReferenceInput>
    );
};

export const WriterInput = ({
    isRequired,
    variant,
    disabled,
    setStateIdCallback,
    hideLink,
    defaultValue,
    helperText,
}: ValidationProps & DisplayProps & CallbackProps & IReferenceInputProps) => {
    return (
        <ReferenceInputWithLink
            source="writer_id"
            reference="v1_Writers"
            sort={{field: 'FullName', order: 'ASC'}}
            hideLink={hideLink}
        >
            <AutocompleteInput
                label="Writer"
                optionText="FullName"
                defaultValue={defaultValue || null}
                parse={(value) => (value === '' ? null : value)}
                filterToQuery={(searchText) => ({FullName: searchText})}
                validate={isRequired ? required() : undefined}
                variant={variant}
                disabled={disabled ?? undefined}
                onChange={
                    setStateIdCallback
                        ? (event) => setStateIdCallback(event)
                        : undefined
                }
                helperText={helperText}
            />
        </ReferenceInputWithLink>
    );
};

export const ContactInput = ({disabled, isRequired}: ValidationProps) => {
    return (
        <SupplierInput
            disabled={disabled}
            isRequired={isRequired}
            label={'Supplier'}
        />
    );
};

export const SupplierInput = ({
    disabled,
    isRequired,
    label = 'Publisher',
    variant,
    setStateIdCallback,
    hideLink,
    helperText,
}: ValidationProps & DisplayProps & CallbackProps & IReferenceInputProps) => {
    return (
        <ReferenceInputWithLink
            source="supplier_id"
            reference="v1_Suppliers"
            filter={{StateCode: 0}}
            sort={{field: 'FullName', order: 'ASC'}}
            disableCache
            hideLink={hideLink}
        >
            <AutocompleteInput
                label={label}
                disabled={disabled}
                optionText="FullName"
                defaultValue={null}
                validate={isRequired ? required() : undefined}
                parse={(value) => (value === '' ? null : value)}
                filterToQuery={(searchText) => ({FullName: searchText})}
                variant={variant}
                onChange={
                    setStateIdCallback
                        ? (event) => setStateIdCallback(event)
                        : undefined
                }
                helperText={helperText}
            />
        </ReferenceInputWithLink>
    );
};

export const LanguageInput = ({
    source,
    label,
    isRequired,
    variant,
}: ReferenceProps & ValidationProps & DisplayProps) => {
    return (
        <ReferenceInput
            source={source}
            reference="v1_new_languagesBase"
            sort={{field: 'new_name', order: 'ASC'}}
        >
            <AutocompleteInput
                label={label}
                optionText="new_name"
                parse={(value) => (value === '' ? null : value)}
                filterToQuery={(searchText) => ({new_name: searchText})}
                validate={isRequired ? required() : undefined}
                variant={variant}
                fullWidth
            />
        </ReferenceInput>
    );
};

export const ProjectInput = ({isRequired}: ValidationProps) => {
    return (
        <ReferenceInputWithLink
            source="project_id"
            reference="v1_Project"
            filter={{statecode: 0}}
            sort={{field: 'new_name', order: 'ASC'}}
        >
            <AutocompleteInput
                label="Project"
                optionText="new_name"
                parse={(value) => (value === '' ? null : value)}
                filterToQuery={(searchText) => ({new_name: searchText})}
                validate={isRequired ? required() : undefined}
            />
        </ReferenceInputWithLink>
    );
};

export const ContentTypeInput = (props: DisplayProps & ValidationProps) => {
    const {defaultValue, helperText, variant} = props;

    const websiteId = useWatch({name: 'website_id'});
    const websiteFilter =
        websiteId === undefined
            ? {}
            : {website_id: websiteId || EMPTY_FILTER_ID};

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(true);
    }, []);

    return (
        <ReferenceInput
            filter={websiteFilter}
            name="contenttype_id"
            reference="v1_ContentTypes"
            sort={{field: 'new_name', order: 'ASC'}}
            source="contenttype_id"
        >
            <AutocompleteInput
                defaultValue={loaded ? defaultValue ?? undefined : undefined}
                filterToQuery={(searchText) => ({
                    new_name: searchText,
                })}
                label="Content Type"
                name="contenttype_id"
                optionText="new_name"
                parse={(value) => (value === '' ? null : value)}
                variant={variant}
                disabled={websiteId === undefined ? false : !websiteId}
                helperText={helperText}
            />
        </ReferenceInput>
    );
};

export const WebsiteInput = (
    props: ValidationProps & DisplayProps & IReferenceInputProps
) => {
    const {isRequired, helperText, defaultValue, variant, hideLink, label} =
        props;

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(true);
    }, []);

    return (
        <ReferenceInputWithLink
            name="website_id"
            perPage={10}
            reference="v1_Website"
            sort={{field: 'new_name', order: 'ASC'}}
            source="website_id"
            hideLink={hideLink}
            urlLinkIcon={props.urlLinkIcon}
        >
            <AutocompleteInput
                create={<CreateWebsiteFromDropdown />}
                defaultValue={loaded ? defaultValue ?? undefined : undefined}
                filterToQuery={(searchText) => ({new_name: searchText})}
                label={label || 'Domain'}
                name="website_id"
                optionText="new_name"
                parse={(value) => (value === '' ? null : value)}
                validate={isRequired ? required() : undefined}
                variant={variant}
                helperText={helperText}
            />
        </ReferenceInputWithLink>
    );
};

export const AffiliateOrderInput = ({
    isRequired,
    disabled,
}: ValidationProps) => {
    return (
        <ReferenceInputWithLink
            source="order_id"
            filter={{StateCode: 0}}
            reference="v1_SalesOrderBase"
            sort={{field: 'Name', order: 'ASC'}}
        >
            <AutocompleteInput
                optionText="Name"
                label="Affiliate Order"
                disabled={disabled}
                defaultValue={null}
                parse={(value) => (value === '' ? null : value)}
                filterToQuery={(searchText) => ({Name: searchText})}
                validate={isRequired ? required() : undefined}
            />
        </ReferenceInputWithLink>
    );
};

export const CurrencyInput = ({
    isRequired,
    disabled,
    variant,
    label,
    source,
    helperText,
    defaultValue,
}: ValidationProps & DisplayProps) => {
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(true);
    }, []);

    return (
        <ReferenceInput
            source={source ? source : 'currency_id'}
            reference="v1_TransactionCurrencyBase"
        >
            <AutocompleteInput
                optionText="CurrencySymbol"
                defaultValue={loaded ? defaultValue ?? undefined : undefined}
                label={label ? label : 'Currency'}
                disabled={disabled}
                filterToQuery={(searchText) => ({CurrencySymbol: searchText})}
                parse={(value) => (value === '' ? null : value)}
                validate={isRequired ? required() : undefined}
                variant={variant}
                helperText={helperText}
            />
        </ReferenceInput>
    );
};

export const PaymentMethodInput = ({isRequired, disabled}: ValidationProps) => {
    return (
        <ReferenceInputWithLink
            source="paymentmethod_id"
            reference="v1_new_paymentmethodBase"
            filter={{statecode: 0}}
            sort={{field: 'new_name', order: 'ASC'}}
        >
            <AutocompleteInput
                optionText="new_name"
                label="Payment Method"
                disabled={disabled}
                defaultValue={null}
                filterToQuery={(searchText) => ({new_name: searchText})}
                parse={(value) => (value === '' ? null : value)}
                validate={isRequired ? required() : undefined}
            />
        </ReferenceInputWithLink>
    );
};

export const CustomerInput = ({isRequired, disabled}: ValidationProps) => {
    return (
        <ReferenceInputWithLink
            source="company_id"
            reference="v1_AccountBase"
            filter={{StateCode: 0}}
            sort={{field: 'Name', order: 'ASC'}}
        >
            <AutocompleteInput
                optionText="Name"
                label="Company"
                disabled={disabled}
                defaultValue={null}
                filterToQuery={(searchText) => ({Name: searchText})}
                parse={(value) => (value === '' ? null : value)}
                validate={isRequired ? required() : undefined}
            />
        </ReferenceInputWithLink>
    );
};

export const BrandInput = ({
    isRequired,
    variant,
    helperText,
}: ValidationProps & DisplayProps) => {
    return (
        <ReferenceInputWithLink
            source="brand_id"
            reference="v1_new_brandBase"
            filter={{statecode: 0}}
            sort={{field: 'new_name', order: 'ASC'}}
        >
            <AutocompleteInput
                optionText="new_name"
                label="Brand"
                filterToQuery={(searchText) => ({new_name: searchText})}
                validate={isRequired ? required() : undefined}
                variant={variant}
                helperText={helperText}
            />
        </ReferenceInputWithLink>
    );
};
